import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSnapshot } from "valtio";
import { useInView } from "react-intersection-observer";
import { format } from "date-fns";
import { createUseStyles } from "react-jss";

import { fetchApi } from "../../../services/api";
import Checkbox from "../inputs/Checkbox";
import TextInput from "../inputs/TextInput";
import Selector from "../../Selector";
import Label from "../Label";
import { currentAccount } from "../../../proxies";

const ITEMS_PER_PAGE = 15;

export const GalleryContext = createContext(() => {});

export const useGallerySelector = (initial = {}) => {
  const [Gallery, setGallery] = useState(initial);
  const context = useContext(GalleryContext);

  return [Gallery, () => context(m => setGallery(m ?? initial)), setGallery];
};

const RecursiveList = ({ body, offset, setResults, event, allGalleries }) => {
  // "waiting" | "loading" | "more" | "done"
  const [status, setStatus] = useState("waiting");
  const current = useRef();
  const currEvt = useRef(event);
  const [ref, inView] = useInView();

  const offsetBody = useMemo(() => {
    if (body) {
      const nBody = new URLSearchParams(body);
      nBody.set("offset", offset);
      return nBody;
    } else {
      return null;
    }
  }, [body, offset]);

  useEffect(() => {
    if (
      (offsetBody !== null &&
        (inView || offsetBody.get("offset") === "0") &&
        offsetBody !== current.current &&
        (event || allGalleries)) ||
      (event !== currEvt.current && event)
    ) {
      const offset = offsetBody?.get?.("offset");
      current.current = offsetBody;
      currEvt.current = event;
      setStatus("loading");
      fetchApi(
        allGalleries ? "/gallery/list" : `/event/${event}/gallery/list`,
        {
          body: offsetBody,
          method: "post",
          version: "v2",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          },
        },
      )
        .then(res => {
          setResults(data =>
            !offset || offset === "0"
              ? res.data
              : [...data, ...res.data],
          );
          setStatus(res.more_results ? "more" : "done");
        })
        .catch(err => {
          console.error(err);
          setStatus("error");
        });
    }
  }, [offsetBody, inView, status, event, setResults, allGalleries]);

  switch (status) {
    case "waiting":
      return <div ref={ref} style={{ height: 200 }} />;
    case "more":
      return (
        <RecursiveList
          body={body}
          offset={offset + ITEMS_PER_PAGE}
          setResults={setResults}
          event={event}
        />
      );
    case "loading":
      return null;
    case "error":
      return <p>An error has occured</p>;
    default:
      return null;
  }
};

const GallerySelector = ({ show, onClose, onSubmit, team }) => {
  const [results, setResults] = useState([]);
  const [search, setSearch] = useState("");
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [query, setQuery] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [event, setEvent] = useState();
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [allGalleries, setAllGalleries] = useState(false);
  const { event: currentEvent } = useSnapshot(currentAccount);
  const debounce = useRef();
  const classes = useStyles({ show });

  // reset results
  useEffect(() => {
    if (show) {
      setResults([]);
      setQuery(null);
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      fetchApi(`/team/${team}/event`, { version: "v2" }).then(res => {
        if (res.length > 0) {
          console.log(currentEvent, res);
          setEvent(res.find(r => r.id === currentEvent)?.token ?? res[0].token);
          setEvents(
            res.map(event => ({ label: event.title, value: event.token })),
          );
        }
        setLoadingEvents(false);
      });
    }
  }, [team, show, currentEvent]);

  useEffect(() => {
    clearTimeout(debounce.current);
    const body = new URLSearchParams();
    // make sure response format is generic for pagination
    body.append("limit", ITEMS_PER_PAGE);
    body.append("_outputFormat", "generic");

    if (search) body.append("search[value]", search);

    if (show) {
      debounce.current = setTimeout(() => setQuery(body), 100);
    }
  }, [show, search]);

  switch (results?.status) {
    case "error":
      console.error(`Could not fetch gallery`);
      setTimeout(onSubmit, 1000);
      return (
        <div className={classes.background}>
          <div className={classes.dim} />
          <div className={classes.loading}>Error fetching galleries</div>
        </div>
      );
    default:
      return (
        <div className={classes.background}>
          <div className={classes.dim} onClick={onClose} />
          <div className={classes.container}>
            <h1>Gallery Selector</h1>
            <div className={classes.row}>
              {!allGalleries && (
                <Label name="Event">
                  <div className={classes.selector}>
                    <Selector
                      options={events}
                      loading={loadingEvents}
                      value={event}
                      onChange={evt => setEvent(evt)}
                      emptyPlaceholder="No events found."
                      expanded={expanded}
                      toggle={() => setExpanded(x => !x)}
                      isTeamSelect
                      light
                    />
                  </div>
                </Label>
              )}
              <TextInput
                label="Search"
                value={search}
                onChange={s => setSearch(s)}
              />
            </div>
            <Checkbox value={allGalleries} onChange={v => setAllGalleries(v)}>
              Include galleries from all events
            </Checkbox>
            <Checkbox value={includeEmpty} onChange={v => setIncludeEmpty(v)}>
              Include galleries with no images
            </Checkbox>
            <div className={classes.results}>
              {results?.filter(x => (includeEmpty ? true : x?.entry_count > 0))
                ?.map(item => (
                  <div
                    key={item.id}
                    className={classes.result}
                    onClick={() => onSubmit(item)}
                  >
                    <div className={classes.info}>
                      <p className={classes.title}>{item.name}</p>
                      <p className={classes.subtitle}>
                        {item.entry_count} images
                        <span>
                          , last modified:{" "}
                          {format(new Date(item.updated_at), "do MMMM yyyy")}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              {!loadingEvents && (
                <RecursiveList
                  allGalleries={allGalleries}
                  event={event}
                  body={query}
                  setResults={setResults}
                  offset={0}
                />
              )}
            </div>
          </div>
        </div>
      );
  }
};

const GallerySelectorProvider = ({ children, team }) => {
  const [showGallery, setShowGallery] = useState(false);
  const [galleryCallback, setGalleryCallback] = useState(() => () => {});

  const selectGallery = useCallback(callback => {
    setShowGallery(true);
    setGalleryCallback(() => (...args) => {
      setShowGallery(false);
      callback(...args);
    });
  }, []);

  return (
    <GalleryContext.Provider value={selectGallery}>
      <GallerySelector
        show={showGallery}
        onSubmit={galleryCallback}
        onClose={() => setShowGallery(false)}
        team={team}
      />
      {children}
    </GalleryContext.Provider>
  );
};

const useStyles = createUseStyles({
  background: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: props => (props.show ? 1 : 0),
    transition: "opacity 0.2s ease",
    pointerEvents: props => (props.show ? "auto" : "none"),
    zIndex: 100,
  },
  dim: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    zIndex: -1,
  },
  loading: {
    color: "white",
  },
  container: {
    width: "90%",
    maxWidth: 700,
    backgroundColor: "#313131",
    paddingBottom: 15,
    borderRadius: 5,
    boxSizing: "border-box",
    "& h1": {
      color: "white",
    },
    "& > *": {
      padding: [0, 15],
    },
  },
  results: {
    width: "100%",
    height: "50vh",
    overflow: "auto",
    boxSizing: "border-box",
    marginTop: 15,
    padding: 0,
    position: "relative",

    "& > p": {
      "text-align": "center",
      color: "white",
    },
  },
  result: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    padding: [5, 15],
    boxSizing: "border-box",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#373737",
      "& p > span": {
        color: "#ccc",
      },
    },
  },
  title: {
    color: "white",
    margin: 0,
    fontSize: 18,
  },
  subtitle: {
    color: "#ccc",
    margin: 0,
    fontSize: 14,
    "& span": {
      color: "transparent",
      transition: "color 0.2s ease",
    },
  },
  message: {
    color: "white",
    margin: 0,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  selector: {
    height: 29,
    display: "flex",
    paddingTop: 3,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    "& label": {
      width: "100%",
      marginRight: 15,
    },
  },
});

export default GallerySelectorProvider;
